import { formatDate } from '@insights-ltd/design-library/utils';
import {
  DatePickerV2,
  Dialog,
  InputSelectV2,
  MenuItem,
  RadioGroupControl,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useState } from 'react';
import {
  Controller,
  RegisterOptions,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { EventResponse, ScheduleType } from 'api';
import {
  isAfterTodayOrDeadlineFieldRules,
  getErrorMessage,
  canSendWeeklyReminder,
  canSendMonthlyReminder,
} from 'components/EventForms/validation';
import { DateTime } from 'luxon';
import Box from '@mui/material/Box';

type EventReminders = {
  reminderAt: DateTime | null;
  schedule: ScheduleType;
};

type FormData = {
  reminderAt: DateTime | null;
  schedule: ScheduleType | '';
};

export type SetReminderDialogProps = {
  event: EventResponse;
  open: boolean;
  onClose: () => void;
  onSetReminder: SubmitHandler<EventReminders>;
  reminderAt: DateTime | null;
};

const SetReminderDialog = ({
  event,
  open,
  onClose,
  onSetReminder,
  reminderAt,
}: SetReminderDialogProps) => {
  const { t, i18n } = useTranslation();
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<FormData>({
    defaultValues: { reminderAt, schedule: '' },
  });

  const [reminderType, setReminderType] = useState('ONE_REMINDER');
  const onSubmit = (data: FormData) => {
    const schedule = reminderType === 'ONE_REMINDER' ? 'ONCE' : data.schedule;
    if (!schedule) {
      throw new Error('Schedule is required');
    }
    onSetReminder({ reminderAt: data.reminderAt, schedule });
  };

  const onModalClose = () => {
    onClose();
    reset();
  };

  return (
    <Dialog
      open={open}
      onClose={onModalClose}
      title={t(
        'ui.event-management.events.learners.set-reminder-modal-schedule.title',
      )}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      content={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextV2 variant="body1" id="alert-dialog-description">
            {t(
              'ui.event-management.events.learners.set-reminder-modal-schedule.content',
            )}
          </TextV2>
          <RadioGroupControl
            name="reminderType"
            title={t(
              'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-reminder',
            )}
            onChange={(value) => setReminderType(value)}
            options={[
              {
                label: t(
                  'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-one',
                ),
                value: 'ONE_REMINDER',
              },
              {
                label: t(
                  'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-recurring',
                ),
                value: 'RECURRING_REMINDERS',
                disabled: !canSendWeeklyReminder(event.deadlineAt),
              },
            ]}
            value={reminderType}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontWeight: 'bold',
              },
            }}
          />
          {reminderType === 'RECURRING_REMINDERS' ? (
            <Controller
              name="schedule"
              control={control}
              rules={{ required: true }}
              render={({ field: { ref, ...rest } }) => (
                <InputSelectV2
                  id="reminder-frequency"
                  labelText={t(
                    'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-message-1',
                  )}
                  required={true}
                  error={Boolean(errors.schedule)}
                  additionalText={
                    errors.schedule
                      ? t(
                          'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-message-2',
                        )
                      : undefined
                  }
                  inputRef={ref}
                  fullWidth={true}
                  {...rest}
                >
                  <MenuItem
                    disabled
                    hidden
                    value=""
                    style={{ display: 'none' }}
                  />
                  <MenuItem value="WEEKLY">
                    {t(
                      'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-weekly',
                    )}
                  </MenuItem>
                  <MenuItem
                    value="MONTHLY"
                    disabled={!canSendMonthlyReminder(event.deadlineAt)}
                  >
                    {t(
                      'ui.event-management.events.learners.set-reminder-modal-schedule.frequency-monthly',
                    )}
                  </MenuItem>
                </InputSelectV2>
              )}
            />
          ) : null}

          <TextV2 variant="body1">
            <Trans
              i18nKey="ui.event-management.events.learners.set-reminder-modal.content-2.bold"
              values={{
                today: formatDate(
                  new Date().toISOString(),
                  undefined,
                  i18n.language,
                ),
                deadline: formatDate(
                  event.deadlineAt,
                  undefined,
                  i18n.language,
                ),
              }}
              components={{
                bold: <TextV2 component="span" variant="bodyBold" />,
              }}
            />
          </TextV2>
          <Box sx={{ '.MuiFormControl-root': { width: '100%' } }}>
            <Controller
              control={control}
              name="reminderAt"
              render={({
                field: { onChange: onReactHookFormChange, value },
              }) => (
                <DatePickerV2
                  error={Boolean(errors.reminderAt)}
                  errorMessage={t(getErrorMessage(errors.reminderAt))}
                  labelText={
                    reminderType === 'RECURRING_REMINDERS'
                      ? t(
                          'ui.event-management.events.learners.set-reminder-modal-schedule.reminder-start-date',
                        )
                      : t(
                          'ui.event-management.events.learners.set-reminder-modal.date-label',
                        )
                  }
                  onChange={(newValue) => {
                    onReactHookFormChange(newValue);
                  }}
                  value={value}
                />
              )}
              rules={
                isAfterTodayOrDeadlineFieldRules(
                  event.deadlineAt,
                ) as RegisterOptions & {
                  deps?: undefined;
                }
              }
            />
          </Box>
        </Box>
      }
      primaryButtonText={t(
        'ui.event-management.events.learners.set-reminder-modal-schedule.action',
      )}
      onSubmit={handleSubmit(onSubmit)}
      secondaryButtonText={t(
        'ui.event-management.events.learners.set-reminder-modal.cancel',
      )}
    />
  );
};

export default SetReminderDialog;
